import { trackEvent } from "@/lib/analytics";

import { GlobalSearchItem, GlobalSearchItemType } from "./GlobalSearch.types";

/**
 * Classify the user's query so we can answer "what shape of thing do people
 * search for?" without ever shipping the raw string to analytics. Raw query
 * strings are PII (emails, phone numbers, names) — we only log the shape.
 *
 * `tournated_tag` matches the canonical `TD-XXXXXX` format (case-insensitive).
 */
export type QueryShape = "email" | "phone" | "uuid" | "numeric_id" | "tournated_tag" | "freetext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PHONE_RE = /^\+?\d[\d\s-]{5,}$/;
const NUMERIC_RE = /^\d+$/;
const TOURNATED_TAG_RE = /^TD-[A-Z0-9]{4,}$/i;

export const classifyQueryShape = (raw: string): QueryShape => {
  const s = raw.trim();
  if (EMAIL_RE.test(s)) return "email";
  if (UUID_RE.test(s)) return "uuid";
  if (PHONE_RE.test(s)) return "phone";
  if (NUMERIC_RE.test(s)) return "numeric_id";
  if (TOURNATED_TAG_RE.test(s)) return "tournated_tag";
  return "freetext";
};

export type SearchOpenTrigger = "focus" | "shortcut_cmd_k" | "mobile_button";

export type SearchCloseOutcome = "selected" | "abandoned";

type CommonProps = {
  searchSessionId: string;
  platformId?: string;
  federationId?: string;
};

const summarizeTypeBreakdown = (items: GlobalSearchItem[]): string => {
  const counts = new Map<GlobalSearchItemType, number>();
  for (const item of items) counts.set(item.type, (counts.get(item.type) ?? 0) + 1);
  // GA4 dimensions are strings; encode as `type:count,type:count` sorted by
  // count desc so high-cardinality combos still group cleanly.
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => `${type}:${count}`)
    .join(",");
};

export const trackSearchOpened = (props: CommonProps & { trigger: SearchOpenTrigger }) => {
  trackEvent("global_search_opened", {
    search_session_id: props.searchSessionId,
    platform_id: props.platformId,
    federation_id: props.federationId,
    trigger: props.trigger,
  });
};

export const trackSearchQueryExecuted = (
  props: CommonProps & {
    keyword: string;
    page: number;
    resultCount: number;
    items: GlobalSearchItem[];
    latencyMs: number;
  }
) => {
  trackEvent("global_search_query_executed", {
    search_session_id: props.searchSessionId,
    platform_id: props.platformId,
    federation_id: props.federationId,
    query_shape: classifyQueryShape(props.keyword),
    query_length: props.keyword.trim().length,
    query_word_count: props.keyword.trim().split(/\s+/).filter(Boolean).length,
    page: props.page,
    result_count: props.resultCount,
    has_results: props.resultCount > 0,
    top_result_type: props.items[0]?.type,
    type_breakdown: summarizeTypeBreakdown(props.items),
    latency_ms: Math.round(props.latencyMs),
  });
};

export const trackSearchResultSelected = (
  props: CommonProps & {
    keyword: string;
    option: GlobalSearchItem;
    position: number;
    positionWithinType: number;
    resultCount: number;
    wasRecent: boolean;
  }
) => {
  trackEvent("global_search_result_selected", {
    search_session_id: props.searchSessionId,
    platform_id: props.platformId,
    federation_id: props.federationId,
    query_shape: props.keyword ? classifyQueryShape(props.keyword) : "empty",
    query_length: props.keyword.trim().length,
    item_type: props.option.type,
    item_id: "id" in props.option ? props.option.id : undefined,
    position: props.position,
    position_within_type: props.positionWithinType,
    result_count: props.resultCount,
    was_recent: props.wasRecent,
    was_page_suggestion: props.option.type === "page",
  });
};

export const trackSearchSecondaryAction = (
  props: CommonProps & {
    option: GlobalSearchItem;
    actionKey: string;
    position: number;
  }
) => {
  trackEvent("global_search_secondary_action_used", {
    search_session_id: props.searchSessionId,
    platform_id: props.platformId,
    federation_id: props.federationId,
    item_type: props.option.type,
    item_id: "id" in props.option ? props.option.id : undefined,
    action_key: props.actionKey,
    position: props.position,
  });
};

export const trackSearchClosed = (
  props: CommonProps & {
    outcome: SearchCloseOutcome;
    lastKeyword: string;
    lastResultCount: number;
    dwellMs: number;
  }
) => {
  trackEvent("global_search_closed", {
    search_session_id: props.searchSessionId,
    platform_id: props.platformId,
    federation_id: props.federationId,
    outcome: props.outcome,
    last_query_shape: props.lastKeyword ? classifyQueryShape(props.lastKeyword) : "empty",
    last_query_length: props.lastKeyword.trim().length,
    last_result_count: props.lastResultCount,
    dwell_ms: Math.round(props.dwellMs),
  });
};
